import { useEffect, type RefObject } from "react";

export type GridPalette = {
  gridPrimary: string;
  gridSecondary: string;
  gridAlpha: number;
  particleColors: string[] | null;
  particleBrightnessRange: [number, number] | null;
  particleOpacityMin: number;
  particleOpacityMax: number;
  particleComposite: GlobalCompositeOperation;
  cursorInner: string;
  cursorOuter: string;
  cursorComposite: GlobalCompositeOperation;
};

type V3 = { x: number; y: number; z: number };
type Cam = { pos: V3; forward: V3; right: V3; up: V3; fov: number };
type Sz = { width: number; height: number };

const MOBILE_MAX_WIDTH = 767;
const MOBILE_MAX_DPR = 1.5;
const MOBILE_ACTIVE_FRAME_INTERVAL_MS = 1000 / 24;
const MOBILE_IDLE_FRAME_INTERVAL_MS = 1000 / 10;
const MOBILE_IDLE_AFTER_MS = 3000;

const clamp = (v: number, lo: number, hi: number) =>
  Math.min(hi, Math.max(lo, v));
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const rand = (lo: number, hi: number) => lo + Math.random() * (hi - lo);

const normalize = (v: V3): V3 => {
  const l = Math.hypot(v.x, v.y, v.z) || 1;
  return { x: v.x / l, y: v.y / l, z: v.z / l };
};

const cross = (a: V3, b: V3): V3 => ({
  x: a.y * b.z - a.z * b.y,
  y: a.z * b.x - a.x * b.z,
  z: a.x * b.y - a.y * b.x,
});

const buildCamera = (): Cam => {
  const pos = { x: 0, y: 6, z: 10 };
  const tgt = { x: 0, y: 0, z: 0 };
  const forward = normalize({
    x: tgt.x - pos.x,
    y: tgt.y - pos.y,
    z: tgt.z - pos.z,
  });
  const right = normalize(cross(forward, { x: 0, y: 1, z: 0 }));
  const up = normalize(cross(right, forward));
  return { pos, forward, right, up, fov: (55 * Math.PI) / 180 };
};

const project = (pt: V3, cam: Cam, sz: Sz) => {
  const dx = pt.x - cam.pos.x,
    dy = pt.y - cam.pos.y,
    dz = pt.z - cam.pos.z;
  const cx = dx * cam.right.x + dy * cam.right.y + dz * cam.right.z;
  const cy = dx * cam.up.x + dy * cam.up.y + dz * cam.up.z;
  const cz = dx * cam.forward.x + dy * cam.forward.y + dz * cam.forward.z;
  if (cz <= 0.1) return null;
  const scale = sz.height / 2 / Math.tan(cam.fov / 2);
  return {
    x: (cx * scale) / cz + sz.width / 2,
    y: (-cy * scale) / cz + sz.height / 2,
    depth: cz,
  };
};

const worldFromScreen = (cam: Cam, sz: Sz, nx: number, ny: number) => {
  const aspect = sz.width / sz.height;
  const tan = Math.tan(cam.fov / 2);
  const cx = nx * aspect * tan,
    cy = -ny * tan,
    cz = 1;
  const dir = normalize({
    x: cam.right.x * cx + cam.up.x * cy + cam.forward.x * cz,
    y: cam.right.y * cx + cam.up.y * cy + cam.forward.y * cz,
    z: cam.right.z * cx + cam.up.z * cy + cam.forward.z * cz,
  });
  if (Math.abs(dir.y) < 1e-4) return null;
  const t = -cam.pos.y / dir.y;
  if (t <= 0) return null;
  return { x: cam.pos.x + dir.x * t, z: cam.pos.z + dir.z * t };
};

const gridExtent = (cam: Cam, sz: Sz) => {
  let max = 0;
  for (const c of [
    { x: -1, y: -1 },
    { x: 1, y: -1 },
    { x: 1, y: 1 },
    { x: -1, y: 1 },
  ]) {
    const p = worldFromScreen(cam, sz, c.x, c.y);
    if (p) max = Math.max(max, Math.abs(p.x), Math.abs(p.z));
  }
  return max || 6;
};

export function useSpacetimeGrid(
  canvasRef: RefObject<HTMLCanvasElement | null>,
  light: GridPalette,
  dark: GridPalette,
  {
    optimizeForMobile = false,
    lowPower = false,
  }: { optimizeForMobile?: boolean; lowPower?: boolean } = {},
) {
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const checkDark = () =>
      !document.documentElement.classList.contains("light");
    let pal = checkDark() ? dark : light;
    const observer = new MutationObserver(() => {
      pal = checkDark() ? dark : light;
      markActivity();
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    const state = {
      width: 0,
      height: 0,
      dpr: 1,
      mouse: { x: 0, y: 0 },
      target: { x: 0, y: 0 },
      mouseScreen: { x: 0, y: 0 },
      mass: { x: 0, z: 0 },
      lastMass: null as { x: number; z: number } | null,
      excitation: 0,
    };

    const particles = Array.from({ length: 40 }, () => ({
      life: 0,
      start: 0,
      x0: 0,
      y0: 0,
      x1: 0,
      y1: 0,
      size: 0,
      opacity: 0,
      color: "",
    }));

    const sim = {
      minX: -6,
      maxX: 6,
      minZ: -6,
      maxZ: 6,
      step: 0.4,
      nx: 0,
      nz: 0,
      u: null as Float32Array | null,
      uPrev: null as Float32Array | null,
      uNext: null as Float32Array | null,
      lastTime: performance.now() / 1000,
      activeRadius: 50,
    };

    let needsGridReset = true;
    let animId = 0;
    let lastActivityTime = performance.now();
    let lastDrawTime = 0;

    const isOptimizedMobile = () =>
      optimizeForMobile && window.innerWidth <= MOBILE_MAX_WIDTH;

    const initGrid = (cam: Cam, sz: Sz) => {
      const ext = gridExtent(cam, sz) + 2;
      sim.minX = -ext;
      sim.maxX = ext;
      sim.minZ = -ext;
      sim.maxZ = ext;
      sim.nx = Math.max(24, Math.floor((sim.maxX - sim.minX) / sim.step) + 1);
      sim.nz = Math.max(24, Math.floor((sim.maxZ - sim.minZ) / sim.step) + 1);
      const n = sim.nx * sim.nz;
      sim.u = new Float32Array(n);
      sim.uPrev = new Float32Array(n);
      sim.uNext = new Float32Array(n);
    };

    const sampleGrid = (x: number, z: number) => {
      if (!sim.u) return 0;
      const fx = (x - sim.minX) / sim.step,
        fz = (z - sim.minZ) / sim.step;
      const ix = Math.floor(fx),
        iz = Math.floor(fz);
      if (ix < 0 || iz < 0 || ix >= sim.nx - 1 || iz >= sim.nz - 1) return 0;
      const tx = fx - ix,
        tz = fz - iz;
      const row = iz * sim.nx,
        i00 = row + ix;
      const ux0 = sim.u[i00] + (sim.u[i00 + 1] - sim.u[i00]) * tx;
      const ux1 =
        sim.u[i00 + sim.nx] +
        (sim.u[i00 + sim.nx + 1] - sim.u[i00 + sim.nx]) * tx;
      return ux0 + (ux1 - ux0) * tz;
    };

    const applyImpulse = (x: number, z: number, strength: number) => {
      if (!sim.u || !sim.uPrev) return;
      const cx = Math.round((x - sim.minX) / sim.step);
      const cz = Math.round((z - sim.minZ) / sim.step);
      if (cx < 2 || cz < 2 || cx >= sim.nx - 2 || cz >= sim.nz - 2) return;
      const r = 2,
        r2 = r * r;
      for (let dz = -r; dz <= r; dz++) {
        const row = (cz + dz) * sim.nx;
        for (let dx = -r; dx <= r; dx++) {
          const d2 = dx * dx + dz * dz;
          if (d2 > r2) continue;
          const delta = strength * Math.exp(-d2 / r2);
          sim.u[row + cx + dx] += delta;
          sim.uPrev[row + cx + dx] += delta;
        }
      }
    };

    const updateWave = (now: number, cam: Cam) => {
      if (!sim.u || !sim.uPrev || !sim.uNext) return;
      const dt = Math.min(0.033, Math.max(0.008, now - sim.lastTime || 0.016));
      sim.lastTime = now;
      const c2 = Math.min(0.28, ((12.0 * dt) / sim.step) ** 2);
      const { nx, nz, u, uPrev, uNext } = sim;
      const centerX = Math.round((cam.pos.x - sim.minX) / sim.step);
      const centerZ = Math.round((cam.pos.z - sim.minZ) / sim.step);
      const radius = Math.max(2, Math.round(sim.activeRadius / sim.step));
      const minX = clamp(centerX - radius, 1, nx - 2);
      const maxX = clamp(centerX + radius, 1, nx - 2);
      const minZ = clamp(centerZ - radius, 1, nz - 2);
      const maxZ = clamp(centerZ + radius, 1, nz - 2);
      const absorbBand = Math.max(4, Math.round(radius * 0.18));
      for (let z = minZ; z <= maxZ; z++) {
        const row = z * nx;
        for (let x = minX; x <= maxX; x++) {
          const i = row + x;
          const lap = u[i - 1] + u[i + 1] + u[i - nx] + u[i + nx] - 4 * u[i];
          let next = 2 * u[i] - uPrev[i] + c2 * lap;
          const dist = Math.hypot(x - centerX, z - centerZ);
          const t = clamp((dist - (radius - absorbBand)) / absorbBand, 0, 1);
          next *= 1 - t * 0.85;
          uNext[i] = next;
        }
      }
      for (let x = 0; x < nx; x++) {
        uNext[x] = 0;
        uNext[(nz - 1) * nx + x] = 0;
      }
      for (let z = 0; z < nz; z++) {
        uNext[z * nx] = 0;
        uNext[z * nx + nx - 1] = 0;
      }
      sim.uPrev = u;
      sim.u = uNext;
      sim.uNext = uPrev;
    };

    const spawnParticle = (
      p: (typeof particles)[0],
      now: number,
      immediate: boolean,
    ) => {
      p.life = rand(2, 6);
      p.start = immediate ? now - rand(0, p.life) : now;
      p.x0 = rand(-0.12, 1.12);
      p.y0 = rand(-0.12, 1.12);
      p.x1 = p.x0 + rand(-0.18, 0.18);
      p.y1 = p.y0 + rand(-0.2, 0.2);
      p.size = rand(1, 5);
      p.opacity = rand(pal.particleOpacityMin, pal.particleOpacityMax);
      if (pal.particleColors) {
        p.color =
          pal.particleColors[
            Math.floor(Math.random() * pal.particleColors.length)
          ];
      } else {
        const b = Math.round(
          rand(
            pal.particleBrightnessRange![0],
            pal.particleBrightnessRange![1],
          ),
        );
        p.color = `${b},${b},${b}`;
      }
    };

    const drawParticles = (now: number) => {
      ctx.save();
      ctx.globalCompositeOperation = pal.particleComposite;
      for (const p of particles) {
        if (!p.life) spawnParticle(p, now, true);
        const age = (now - p.start) / p.life;
        if (age < 0) continue;
        if (age >= 1) {
          spawnParticle(p, now, false);
          continue;
        }
        const ease = age * age * (3 - 2 * age);
        const peak = age < 0.5 ? age * 2 : (1 - age) * 2;
        const glow = peak * peak * (3 - 2 * peak);
        const x = lerp(p.x0, p.x1, ease) * state.width;
        const y = lerp(p.y0, p.y1, ease) * state.height;
        const size = lerp(p.size * 0.7, p.size * 1.1, ease) * state.dpr;
        const alpha = p.opacity * glow;
        if (alpha < 0.01) continue;
        ctx.globalAlpha = alpha;
        ctx.fillStyle = `rgba(${p.color},${alpha})`;
        ctx.beginPath();
        ctx.arc(x, y, size, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
    };

    const resize = () => {
      const deviceDpr = window.devicePixelRatio || 1;
      state.dpr =
        lowPower || isOptimizedMobile()
          ? Math.min(deviceDpr, MOBILE_MAX_DPR)
          : deviceDpr;
      state.width = Math.floor(window.innerWidth * state.dpr);
      state.height = Math.floor(window.innerHeight * state.dpr);
      canvas.width = state.width;
      canvas.height = state.height;
      particles.forEach((p) =>
        spawnParticle(p, performance.now() / 1000, true),
      );
      needsGridReset = true;
    };

    // Idle wander — prime-frequency oscillators for non-repeating path
    const TAU = Math.PI * 2;
    const idleSeed = { x: Math.random() * 1000, y: Math.random() * 1000 };
    let lastMoveTime = 0;
    let idleBlend = 1; // start in idle mode
    const IDLE_TIMEOUT = 3;

    const idlePos = (t: number) => ({
      x:
        0.6 * Math.sin((TAU * t) / 7 + idleSeed.x) +
        0.3 * Math.sin((TAU * t) / 17 + 1.3) +
        0.15 * Math.cos((TAU * t) / 29 + 2.7),
      y:
        0.5 * Math.sin((TAU * t) / 11 + idleSeed.y) +
        0.25 * Math.cos((TAU * t) / 19 + 2.1) +
        0.12 * Math.sin((TAU * t) / 31 + 4.3),
    });

    const onMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      state.target.x = clamp(
        ((e.clientX - rect.left) / rect.width) * 2 - 1,
        -1,
        1,
      );
      state.target.y = clamp(
        ((e.clientY - rect.top) / rect.height) * 2 - 1,
        -1,
        1,
      );
      state.mouseScreen.x = (e.clientX - rect.left) * state.dpr;
      state.mouseScreen.y = (e.clientY - rect.top) * state.dpr;
      lastMoveTime = performance.now() / 1000;
      markActivity();
    };

    const draw = (frameTime: number) => {
      animId = 0;
      if (document.hidden) return;
      const nowMs = performance.now();
      const optimizedMobile = isOptimizedMobile();
      const mobileFrameInterval =
        nowMs - lastActivityTime >= MOBILE_IDLE_AFTER_MS
          ? MOBILE_IDLE_FRAME_INTERVAL_MS
          : MOBILE_ACTIVE_FRAME_INTERVAL_MS;
      if (
        (lowPower || optimizedMobile) &&
        frameTime - lastDrawTime < mobileFrameInterval
      ) {
        animId = requestAnimationFrame(draw);
        return;
      }
      lastDrawTime = frameTime;
      const now = nowMs / 1000;

      // Blend between real mouse and idle wander
      const idleDt = now - lastMoveTime;
      const targetBlend = idleDt > IDLE_TIMEOUT ? 1 : 0;
      idleBlend = lerp(
        idleBlend,
        targetBlend,
        targetBlend > idleBlend ? 0.015 : 0.06,
      );

      const wander = idlePos(lowPower ? now * 0.5 : now);
      const tx = lerp(state.target.x, wander.x, idleBlend);
      const ty = lerp(state.target.y, wander.y, idleBlend);

      state.mouse.x = lerp(state.mouse.x, tx, 0.08);
      state.mouse.y = lerp(state.mouse.y, ty, 0.08);

      // Update screen-space cursor for light effect during idle
      if (idleBlend > 0.5) {
        state.mouseScreen.x = ((wander.x + 1) / 2) * state.width;
        state.mouseScreen.y = ((wander.y + 1) / 2) * state.height;
      }
      const cam = buildCamera();
      const sz = { width: state.width, height: state.height };
      if (needsGridReset) {
        initGrid(cam, sz);
        needsGridReset = false;
      }

      const massPoint = worldFromScreen(cam, sz, state.mouse.x, state.mouse.y);
      if (massPoint) state.mass = massPoint;
      if (state.lastMass && massPoint) {
        const dx = massPoint.x - state.lastMass.x,
          dz = massPoint.z - state.lastMass.z;
        const dt = Math.max(0.008, now - sim.lastTime);
        const speed = Math.hypot(dx, dz) / dt;
        state.excitation = lerp(
          state.excitation,
          0.22 + clamp(speed * 0.08, 0, 1.2),
          0.08,
        );
        applyImpulse(
          massPoint.x,
          massPoint.z,
          -Math.sin(now * 18.0) * state.excitation,
        );
      }
      state.lastMass = massPoint
        ? { x: massPoint.x, z: massPoint.z }
        : state.lastMass;
      updateWave(now, cam);

      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, state.width, state.height);
      drawParticles(now);

      // Cursor light
      const lx = state.mouseScreen.x,
        ly = state.mouseScreen.y;
      const lr =
        Math.max(state.width, state.height) * 0.5 * (ly / state.height);
      const grad = ctx.createRadialGradient(lx, ly, lr * 0.15, lx, ly, lr);
      grad.addColorStop(0, pal.cursorInner);
      grad.addColorStop(1, pal.cursorOuter);
      ctx.save();
      ctx.globalCompositeOperation = pal.cursorComposite;
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, state.width, state.height);
      ctx.restore();

      // Grid lines
      ctx.lineWidth = 1;
      const step = 0.6;
      const fogAlpha = (d: number) => 1 - clamp((d - 4) / 24, 0, 1);
      const gs = gridExtent(cam, sz) + 2;
      const disp = (x: number, z: number) => {
        if ((x - cam.pos.x) ** 2 + (z - cam.pos.z) ** 2 > sim.activeRadius ** 2)
          return 0;
        return sampleGrid(x, z);
      };

      for (let x = -gs; x <= gs + 0.001; x += step) {
        let prev: { x: number; y: number; depth: number } | null = null;
        for (let z = -gs; z <= gs + 0.001; z += step / 2) {
          const y = disp(x, z);
          const p = project({ x, y, z }, cam, sz);
          if (!p) {
            prev = null;
            continue;
          }
          if (prev) {
            const a =
              ((fogAlpha(prev.depth) + fogAlpha(p.depth)) / 2) * pal.gridAlpha -
              y * 0.5 * Math.abs(ly / state.height);
            if (a > 0.002) {
              ctx.strokeStyle = `rgba(${pal.gridPrimary},${a})`;
              ctx.beginPath();
              ctx.moveTo(prev.x, prev.y);
              ctx.lineTo(p.x, p.y);
              ctx.stroke();
            }
          }
          prev = p;
        }
      }
      for (let z = -gs; z <= gs + 0.001; z += step) {
        let prev: { x: number; y: number; depth: number } | null = null;
        for (let x = -gs; x <= gs + 0.001; x += step / 2) {
          const y = disp(x, z);
          const p = project({ x, y, z }, cam, sz);
          if (!p) {
            prev = null;
            continue;
          }
          if (prev) {
            const a =
              ((fogAlpha(prev.depth) + fogAlpha(p.depth)) / 2) * pal.gridAlpha -
              y * 0.6 * Math.abs(ly / state.height);
            if (a > 0.002) {
              ctx.strokeStyle = `rgba(${pal.gridSecondary},${a})`;
              ctx.beginPath();
              ctx.moveTo(prev.x, prev.y);
              ctx.lineTo(p.x, p.y);
              ctx.stroke();
            }
          }
          prev = p;
        }
      }

      animId = requestAnimationFrame(draw);
    };

    const markActivity = () => {
      lastActivityTime = performance.now();
      if (!animId && !document.hidden) animId = requestAnimationFrame(draw);
    };

    const onVisibilityChange = () => {
      if (document.hidden) {
        cancelAnimationFrame(animId);
        animId = 0;
      } else {
        sim.lastTime = performance.now() / 1000;
        markActivity();
      }
    };

    const onResize = () => {
      resize();
      markActivity();
    };

    resize();
    if (!document.hidden) animId = requestAnimationFrame(draw);
    window.addEventListener("resize", onResize);
    window.addEventListener("mousemove", onMove);
    document.addEventListener("visibilitychange", onVisibilityChange);
    if (lowPower || optimizeForMobile) {
      window.addEventListener("pointerdown", markActivity, { passive: true });
      window.addEventListener("pointermove", markActivity, { passive: true });
      window.addEventListener("scroll", markActivity, { passive: true });
      window.addEventListener("keydown", markActivity);
    }

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("mousemove", onMove);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      if (lowPower || optimizeForMobile) {
        window.removeEventListener("pointerdown", markActivity);
        window.removeEventListener("pointermove", markActivity);
        window.removeEventListener("scroll", markActivity);
        window.removeEventListener("keydown", markActivity);
      }
      observer.disconnect();
    };
  }, [canvasRef, dark, light, optimizeForMobile, lowPower]);
}
